import React, { useEffect, useRef, useState } from 'react';
import bemCn from 'bem-cn';
import { monthNames } from '../../consts/months.consts';
import { IMG_RESIZES } from '../../consts/resizer.consts';
import { ImageHelper } from '../../classes/image-helper.class';
import SniffButton from '../sniff-button';
import PictureSet from '../picture-set';
import SeoReview from '../seo-review';
import Rating from '../rating/index';
import SvgHostPets from '../../assets/svg/SvgHostPets.svg';
import SvgPin from '../../assets/svg/SvgPin.svg';
import './style.scss';

const b = bemCn('review-card');
const bseo = bemCn('review-card-seo');

const isEllipsisActive = (el) => {
    const clone = el.cloneNode(true);
    const style = window.getComputedStyle(el);

    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.width = style.width; // keep same width
    clone.style.whiteSpace = style.whiteSpace;
    clone.style.lineHeight = style.lineHeight;

    // Remove the clamp to get natural height
    clone.style.webkitLineClamp = 'unset';
    clone.style.display = 'block';
    clone.style.overflow = 'visible';

    el.parentElement.appendChild(clone);

    const fullHeight = clone.getBoundingClientRect().height;
    const actualHeight = el.getBoundingClientRect().height;

    clone.remove();
    return fullHeight > actualHeight;
};

const ReviewCard = ({ review, useEffect: useEffectSSR, useState: useStateSSR, useRef: useRefSSR, adLanding, showPhotos, spot, homepage }) => {
    const useRefC = useRefSSR || useRef;
    const useEffectC = useEffectSSR || useEffect;
    const useStateC = useStateSSR || useState;

    const textRef = useRefC(null);
    const [needExpand, setNeedExpand] = useStateC(false);
    const revDate = review.submittedAt || review.createdAt;
    const dateObj = new Date(revDate);
    const utcMonth = dateObj.getUTCMonth();
    const utcYear = dateObj.getUTCFullYear();
    const date = `${monthNames[utcMonth]} ${utcYear}`;
    const hasPhotos = review.photos && review.photos.length > 0;
    const altPhoto = `${review.spot?.title} reviews by ${review.providedBy?.nameAndInitial} | Sniffspot`;

    useEffectC(() => {
        if (textRef?.current) {
            setNeedExpand(isEllipsisActive(textRef.current));
        }
    }, [textRef]);

    const expandReview = () => {
        textRef.current.classList.toggle('text-expanded');
        setNeedExpand(false);
    };

    if (!review) {
        return null;
    }

    return (
        <div className={bseo()}>
            {!homepage && review.stars > 0 && <SeoReview {...{ ...review, submittedAt: revDate, user: review.providedBy }} />}
            <div className="d-flex mb-2">
                <PictureSet
                    imgs={ImageHelper.getUrlFromOptions(review.providedBy.avatarAws, `full-fit-in/${IMG_RESIZES.x064}`)}
                    alt={`Avatar of ${review.providedBy?.nameAndInitial} for Sniffspot`}
                    className={b('avatar')}
                    isAvatar
                    width={56}
                    height={56}
                    useState={useStateSSR}
                    SSR={!!useStateSSR}
                />
                <div>
                    <div className="snif-p snif-semibold text-dark mb-05">{review.providedBy.nameAndInitial}</div>
                    <div className="snif-s2 snif-regular text-medium">{date}</div>
                </div>
            </div>
            <Rating className="mb-2" value={review.stars} showNmb={false} />
            {adLanding && (
                <>
                    <p className="d-flex snif-s2 snif-medium text-deep mb-1 pb-05">
                        <SvgHostPets className="text-dark me-05" width={16} height={16} />
                        {spot.title}
                    </p>
                    <p className="d-flex snif-s2 snif-medium text-dark mb-2">
                        <SvgPin className="me-05" width={16} height={16} />
                        {spot.city}, {spot.state}
                    </p>
                </>
            )}
            <div ref={textRef} className="review-text snif-s1 snif-regular text-dark">
                {review.comment}
            </div>
            {needExpand && (
                <SniffButton
                    className={`review-expand-btn ${(adLanding || showPhotos) && hasPhotos ? 'mb-2' : ''}`}
                    size="sm"
                    color="secondary"
                    onClick={expandReview}
                >
                    Read more
                </SniffButton>
            )}
            {hasPhotos && (adLanding || showPhotos) && (
                <div className={bseo('photo')}>
                    <PictureSet
                        imgs={ImageHelper.getUrlFromOptions(review.photos[0]?.imageAws, `fit-in/${IMG_RESIZES.x0450}`)}
                        height={200}
                        alt={altPhoto}
                    />
                </div>
            )}
        </div>
    );
};

export default ReviewCard;
